import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { AlignCenter, AlignLeft, AlignRight, Bold, Eraser, Heading2, Heading3, Highlighter, Italic, Link2, List, ListOrdered, Minus, Pilcrow, Quote, Redo2, Underline as UnderlineIcon, Undo2 } from "lucide-react";

function ToolbarButton({ editor, action, active, label, testId, children, disabled = false }) {
  return (
    <button type="button" data-testid={testId} aria-label={label} aria-pressed={active} title={label} disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={action} className={`editor-toolbar-button ${active ? "editor-toolbar-button-active" : ""}`}>
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Highlight,
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ["http", "https", "mailto"],
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "<p></p>",
    editorProps: {
      attributes: {
        class: "article-rich-text editor-rich-text",
        "data-testid": "article-rich-text-editable",
        "aria-label": "Article body rich text editor",
      },
    },
    onUpdate: ({ editor: currentEditor }) => onChange(currentEditor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "<p></p>", false);
    }
  }, [editor, value]);

  if (!editor) return <div className="border border-[#D8D0C3] bg-white p-5 text-sm text-[#524842]">Loading editor...</div>;

  const setLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("Enter the link URL", previous || "https://");
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url.trim(), target: "_blank", rel: "noopener noreferrer" }).run();
  };

  const plainText = editor.getText().trim();
  const wordCount = plainText ? plainText.split(/\s+/).length : 0;

  return (
    <div data-testid="article-rich-text-editor" className="overflow-hidden border border-[#D8D0C3] bg-white">
      <div data-testid="rich-text-toolbar" className="editor-toolbar" aria-label="Article formatting toolbar">
        <ToolbarButton editor={editor} testId="format-paragraph-button" label="Paragraph" active={editor.isActive("paragraph")} action={() => editor.chain().focus().setParagraph().run()}><Pilcrow size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-heading-2-button" label="Heading 2" active={editor.isActive("heading", { level: 2 })} action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-heading-3-button" label="Heading 3" active={editor.isActive("heading", { level: 3 })} action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></ToolbarButton>
        <span className="editor-toolbar-divider" />
        <ToolbarButton editor={editor} testId="format-bold-button" label="Bold" active={editor.isActive("bold")} action={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-italic-button" label="Italic" active={editor.isActive("italic")} action={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-underline-button" label="Underline" active={editor.isActive("underline")} action={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-highlight-button" label="Highlight" active={editor.isActive("highlight")} action={() => editor.chain().focus().toggleHighlight().run()}><Highlighter size={16} /></ToolbarButton>
        <span className="editor-toolbar-divider" />
        <ToolbarButton editor={editor} testId="format-bullet-list-button" label="Bulleted list" active={editor.isActive("bulletList")} action={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-numbered-list-button" label="Numbered list" active={editor.isActive("orderedList")} action={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-blockquote-button" label="Blockquote" active={editor.isActive("blockquote")} action={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></ToolbarButton>
        <span className="editor-toolbar-divider" />
        <ToolbarButton editor={editor} testId="format-align-left-button" label="Align left" active={editor.isActive({ textAlign: "left" })} action={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-align-center-button" label="Align center" active={editor.isActive({ textAlign: "center" })} action={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-align-right-button" label="Align right" active={editor.isActive({ textAlign: "right" })} action={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={16} /></ToolbarButton>
        <span className="editor-toolbar-divider" />
        <ToolbarButton editor={editor} testId="format-link-button" label="Add or edit link" active={editor.isActive("link")} action={setLink}><Link2 size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-divider-button" label="Horizontal divider" action={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={16} /></ToolbarButton>
        <span className="editor-toolbar-divider" />
        <ToolbarButton editor={editor} testId="format-undo-button" label="Undo" disabled={!editor.can().undo()} action={() => editor.chain().focus().undo().run()}><Undo2 size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-redo-button" label="Redo" disabled={!editor.can().redo()} action={() => editor.chain().focus().redo().run()}><Redo2 size={16} /></ToolbarButton>
        <ToolbarButton editor={editor} testId="format-clear-button" label="Clear formatting" action={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><Eraser size={16} /></ToolbarButton>
      </div>
      <EditorContent editor={editor} />
      <div data-testid="editor-word-count" className="flex items-center justify-between border-t border-[#E5DFD3] bg-[#FDFBF7] px-4 py-2.5 text-xs text-[#8A7D70]">
        <span>{wordCount} {wordCount === 1 ? "word" : "words"}</span>
        <span>≈ {Math.max(1, Math.ceil(wordCount / 200))} min read</span>
      </div>
    </div>
  );
}
